import { CONNECTOR_OP_LIMITS } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Button, Field, Icon, Input } from "@sagot/ui";
import type { ConnectorCallStep } from "../types";
import { missingParams, OPERATION_EXAMPLES, paramTooLong, withParam, type ParamSpec } from "./connector-op-model";

const C = "engage.workflows.connector.op.params";

interface ParamsProps {
  step: ConnectorCallStep;
  specs: readonly ParamSpec[];
  disabled: boolean;
  onChange: (step: ConnectorCallStep) => void;
}

function hintOf(spec: ParamSpec, t: (key: string, options?: Record<string, unknown>) => string): string {
  const kind = t(`${C}.types.${spec.type}`);
  const options = spec.options.length > 0 ? ` ${t(`${C}.oneOf`, { values: spec.options.join(", ") })}` : "";
  return `${spec.description ? `${spec.description} ` : ""}${kind}.${options}`;
}

/** The typed parameters of the chosen operation: one field per parameter its schema declares, each a template over the run's facts. */
export function OperationParams({ step, specs, disabled, onChange }: ParamsProps) {
  const { t } = useTranslation();
  const missing = new Set(missingParams(step, specs));
  const known = new Set(specs.map((spec) => spec.name));
  const stale = Object.keys(step.params ?? {}).filter((name) => !known.has(name));
  return (
    <fieldset className="space-y-3" data-testid="op-params">
      <legend className="label">{t(`${C}.title`)}</legend>
      <p className="hint -mt-1">{t(`${C}.hint`, { example: OPERATION_EXAMPLES.param })}</p>
      {specs.length === 0 ? <p className="rounded-md bg-surface-2 p-3 text-xs text-fg-muted" data-testid="op-params-none">{t(`${C}.none`)}</p> : null}
      {specs.map((spec) => {
        const value = step.params?.[spec.name] ?? "";
        const error = paramTooLong(value) ? t(`${C}.tooLong`, { max: CONNECTOR_OP_LIMITS.paramValue }) : missing.has(spec.name) ? t(`${C}.required`) : undefined;
        return (
          <Field key={spec.name} label={spec.required ? t(`${C}.requiredLabel`, { name: spec.name }) : spec.name} hint={hintOf(spec, t)} error={error}>
            <Input
              className="font-mono text-xs"
              value={value}
              disabled={disabled}
              maxLength={CONNECTOR_OP_LIMITS.paramValue + 1}
              placeholder={spec.options[0] ?? OPERATION_EXAMPLES.param}
              data-testid={`op-param-${spec.name}`}
              onChange={(event) => { onChange(withParam(step, spec.name, event.target.value)); }}
            />
          </Field>
        );
      })}
      {stale.map((name) => (
        <div key={name} className="flex items-center justify-between gap-2 rounded-md bg-warning-soft p-3 text-xs" role="note" data-testid="op-param-stale">
          <span className="flex items-start gap-2">
            <Icon name="alert-triangle" className="mt-0.5 size-3.5 shrink-0" />
            {t(`${C}.stale`, { name })}
          </span>
          <Button type="button" variant="ghost" size="sm" disabled={disabled} onClick={() => { onChange(withParam(step, name, "")); }}>{t(`${C}.removeStale`)}</Button>
        </div>
      ))}
    </fieldset>
  );
}
